// THE FOUNDATION CLIENT, because these are t1 PATHS. The endpoints moved from t6 to t1 when the
// sharing guard did -- the routes are hand-written terraform under t1's own gateway -- but the
// CLIENT was left pointing at REACT_APP_ADMIN_API_URL, so every call went to api.admin asking for a
// path only api.foundation serves. It fails at the preflight, which reports status 0 and looks like
// a network problem rather than a wrong host.
import AxiosFoundationAPI from "../../../../util/AxiosFoundationAPI";
import {V1UserGroupMembershipIDOCToArray} from "../../../../util/Payload";
import EnterpriseComponent from "../../EnterpriseComponent";
import {SessionContext} from "../../../../util/SessionContext";
import {v4 as uuidv4} from "uuid";


// SourceType names the OBJECT a membership hangs off. It stays "Project Plan" by default so every
// existing caller is unchanged; other objects (Foundation object revisions) set their own via
// setSourceType() so their shares are stored under their own type rather than lying about being
// project plans.
const SOURCE_TYPE = "Project Plan";

// T1 RECORDS GO THROUGH T1 APIs. These shares hang off an ObjectRevision, which is a
// Foundation record, so they are Foundation's to authorise -- t6 is the administration
// app and its endpoints are for administering positions and users, not for writing another
// tier's records. Pointing here also puts the write behind t1's stewardship guard: the t6
// route had no enforcement at all, so anybody could share any object with any position.
class UserGroupMembershipEnterpriseComponent extends EnterpriseComponent {
    sourceType = SOURCE_TYPE;

    setSourceType(value) {
        this.sourceType = value || SOURCE_TYPE;
    }

    getSourceType() {
        return this.sourceType;
    }
    performQuery = (page, pageSize, _) => {
        page = page || 1;
        pageSize = pageSize || this.getDefaultCacheSize();

        this.notifyLoadStartListeners();

        let QBE = {
            "PlatformMessage": {
                "-idoc": this.getIDOCResponse() ?? "v1.UserGroupMembership-001",
                "-id": uuidv4(),
                "UserGroupMembership": {
                    '_attributes': {
                        'limit': pageSize,
                        'sort': this.getFormattedSortSpecification(),
                        'offset': (page - 1) * pageSize
                    },
                    "QueryGroups": {
                        "QueryGroup": [
                            {
                                "Name": "primary",
                                "Operator": "AND",
                                "Type": "QueryGroup",
                                "Membership": {
                                    "Member": []
                                }
                            }
                        ]
                    }
                }
            }
        }
        let queryPayload = this.prepareQueryPayload();

        if (queryPayload.size > 0) {
            for (const [key, value] of queryPayload) {
                if (key.includes("_query")) continue;

                let operator = "EQUAL";
                if (queryPayload.get(key + "_query") !== undefined) {
                    operator = queryPayload.get(key + "_query")
                }
                QBE.PlatformMessage.UserGroupMembership.QueryGroups.QueryGroup[0].Membership.Member.push({
                    "Type": "Attribute",
                    "Name": key,
                    "Value": value,
                    "Operator": operator
                })
            }
        }
        AxiosFoundationAPI({
            method: "GET",
            url: "t1/v1/user_group_memberships",
            json: true,
            params: {
                "QBE": btoa(JSON.stringify(QBE))
            },
            headers: {
                "idoc-response": this.getIDOCResponse() ?? "v1.UserGroupMembership-001",
            },
        }).then(response => {
            this.fetchEnterpriseComponentSuccess(V1UserGroupMembershipIDOCToArray(response.data), page, pageSize);
        }).catch(error => {
            this.fetchEnterpriseComponentFailed(error);
        });
    }

    enterpriseComponentPutIDOC = () => {
        let payload = {};

        payload.PlatformMessage = {};
        payload.PlatformMessage["-idoc"] = "v1.UserGroupMembership-001";
        payload.PlatformMessage.UserGroupMembership = this.preparePUTPayload();

        return payload;
    };

    performUpdate() {
        let payload = this.enterpriseComponentPutIDOC();

        let options = {
            method: "PUT",
            url: "t1/v1/user_group_memberships",
            json: true,
            data: payload,
            headers: {
                "idoc-request": "v1.UserGroupMembership-001",
                "idoc-response": "v1.UserGroupMembership-001",
            },
        };
        this.notifyUpdateStartListeners();
        AxiosFoundationAPI(options)
            .then(response => {
                this.updateEnterpriseComponentSuccess(V1UserGroupMembershipIDOCToArray(response.data));
            })
            .catch(error => {
                this.updateEnterpriseComponentFailed(error);
            });
    };

    enterpriseComponentPostIDOC = () => {
        let payload = {};

        let sessionContext = new SessionContext();

        let adminUserGroupMembership = {};

        adminUserGroupMembership["USID"] = this.getAttributeValue("USID");
        adminUserGroupMembership["SourceUSID"] = sessionContext.getGeneralPurposeVariable();
        adminUserGroupMembership["SourceType"] = this.sourceType;
        adminUserGroupMembership["UserGroupUSID"] = this.getAttributeValue("UserGroupUSID")

        payload.PlatformMessage = {};
        payload.PlatformMessage["-idoc"] = "v1.UserGroupMembership-001";
        payload.PlatformMessage.UserGroupMembership = adminUserGroupMembership;

        return payload;
    };

    performInsert() {
        let payload = this.enterpriseComponentPostIDOC();

        let options = {
            method: "POST",
            url: "t1/v1/user_group_memberships",
            json: true,
            data: payload,
            headers: {
                "idoc-request": "v1.UserGroupMembership-001",
                "idoc-response": "v1.UserGroupMembership-002"
            },
        };
        this.notifyInsertStartListeners();
        AxiosFoundationAPI(options)
            .then(response => {
                this.insertEnterpriseComponentSuccess(V1UserGroupMembershipIDOCToArray(response.data));
            })
            .catch(error => {
                this.insertEnterpriseComponentFailed(error);
            });
    };

    enterpriseComponentDeleteIDOC = () => {
        let payload = {};

        let adminUserGroupMembership = {};

        adminUserGroupMembership["USID"] = this.getAttributeValue("USID");

        payload.PlatformMessage = {};
        payload.PlatformMessage["-idoc"] = "v1.UserGroupMembership-001";
        payload.PlatformMessage.UserGroupMembership = adminUserGroupMembership;

        return payload;
    };

    performDelete() {
        let payload = this.enterpriseComponentDeleteIDOC();

        let options = {
            method: "DELETE",
            url: "t1/v1/user_group_memberships",
            json: true,
            data: payload,
            headers: {
                "idoc-request": "v1.UserGroupMembership-001"
            },
        };
        this.notifyDeleteStartListeners();
        AxiosFoundationAPI(options)
            .then(() => {
                this.deleteEnterpriseComponentSuccess();
            })
            .catch(error => {
                this.deleteEnterpriseComponentFailed(error);
            });
    };
}

export default UserGroupMembershipEnterpriseComponent;